import { useState, useRef, useEffect, useCallback } from 'react'
import { RefreshCw } from 'lucide-react'
import { IconLabel } from '@/components/ui/app-icon'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import type { TemplateData, PreviewTestData } from './types'
import { DEFAULT_TEST_DATA } from './types'
import { templatesApi } from '@/lib/api'
import { beginRenderHistory, finishRenderHistory } from '@/stores/renderHistory.store'
import { isAxiosError } from 'axios'

interface Props {
  isOpen: boolean
  onClose: () => void
  getTemplate: () => TemplateData
}

export function PreviewModal({ isOpen, onClose, getTemplate }: Props) {
  const [testData, setTestData] = useState<PreviewTestData>({ ...DEFAULT_TEST_DATA })
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateField = <K extends keyof PreviewTestData>(key: K, value: PreviewTestData[K]) => {
    setTestData(prev => ({ ...prev, [key]: value }))
  }

  const renderPreview = useCallback(async () => {
    setLoading(true)
    setError(null)
    const templateName =
      (getTemplate() as { template_name?: string }).template_name?.trim() || 'без имени'
    const trackId = beginRenderHistory('preview', templateName)
    try {
      const res = await templatesApi.renderPreview(
        getTemplate() as unknown as Record<string, unknown>,
        testData as unknown as Record<string, unknown>,
      )
      const image = res.data?.image
      if (image) {
        setPreviewSrc(image)
        finishRenderHistory(trackId, 'ok')
      } else {
        setError('Ошибка генерации')
        finishRenderHistory(trackId, 'error', 'Пустой ответ')
      }
    } catch (err) {
      const msg = isAxiosError(err) ? err.message : 'Ошибка загрузки предпросмотра'
      setError(msg)
      setPreviewSrc(null)
      finishRenderHistory(trackId, 'error', msg)
    } finally {
      setLoading(false)
    }
  }, [getTemplate, testData])

  const prevOpen = useRef(false)
  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      void renderPreview()
    }
    prevOpen.current = isOpen
  }, [isOpen, renderPreview])

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-5xl">
      <Modal.Header onClose={onClose}><IconLabel icon="preview">Предпросмотр этикетки</IconLabel></Modal.Header>
      <Modal.Body className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x">
          <div className="flex items-center justify-center min-h-[300px] p-6 bg-gray-50">
            {loading ? (
              <div className="text-center space-y-2">
                <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-sm text-gray-500">Генерация этикетки...</p>
              </div>
            ) : error ? (
              <p className="text-red-500 text-sm text-center">{error}</p>
            ) : previewSrc ? (
              <img src={previewSrc} alt="Предпросмотр" className="max-w-full max-h-[60vh] border shadow" />
            ) : (
              <p className="text-gray-400 text-sm">Нажмите «Обновить» для генерации</p>
            )}
          </div>

          <div className="p-4 overflow-y-auto max-h-[70vh] space-y-3">
            <h3 className="font-semibold text-sm text-gray-700"><IconLabel icon="sandbox">Тестовые данные</IconLabel></h3>

            <Field label="Название товара:" value={testData.product_name} onChange={v => updateField('product_name', v)} />
            <Field label="Код товара:" value={testData.product_code} onChange={v => updateField('product_code', v)} />
            <Field label="GTIN (14 цифр):" value={testData.gtin} onChange={v => updateField('gtin', v)} />
            <Field label="Номер задания:" value={testData.task_id} onChange={v => updateField('task_id', v)} />
            <Field label="Название задания:" value={testData.task_name} onChange={v => updateField('task_name', v)} />

            <div>
              <label className="block text-xs text-gray-600 mb-0.5">КМ Код (полный):</label>
              <textarea
                className="w-full border rounded px-2 py-1 text-sm"
                rows={2}
                value={testData.km_code}
                onChange={e => updateField('km_code', e.target.value)}
              />
            </div>

            <Field label="КА Код:" value={testData.ka_code} onChange={v => updateField('ka_code', v)} />

            <div className="grid grid-cols-2 gap-2">
              <Field label="Дата производства:" value={testData.production_date} onChange={v => updateField('production_date', v)} />
              <Field label="Срок годности:" value={testData.expiry_date} onChange={v => updateField('expiry_date', v)} />
            </div>

            <NumField label="Порядковый номер КМ:" value={testData.sequence_number} onChange={v => updateField('sequence_number', v)} />

            <div className="grid grid-cols-2 gap-2">
              <NumField label="Первый номер КМ:" value={testData.first_sequence} onChange={v => updateField('first_sequence', v)} />
              <NumField label="Последний номер КМ:" value={testData.last_sequence} onChange={v => updateField('last_sequence', v)} />
            </div>

            <Field label="Диапазон номеров:" value={testData.sequence_range} onChange={v => updateField('sequence_range', v)} />

            <div className="grid grid-cols-2 gap-2">
              <NumField label="Первая коробка:" value={testData.first_box} onChange={v => updateField('first_box', v)} />
              <NumField label="Последняя коробка:" value={testData.last_box} onChange={v => updateField('last_box', v)} />
            </div>

            <Field label="Диапазон коробок:" value={testData.box_range} onChange={v => updateField('box_range', v)} />

            <div className="grid grid-cols-2 gap-2">
              <NumField label="Номер коробки:" value={testData.box_num} onChange={v => updateField('box_num', v)} />
              <NumField label="Номер паллеты:" value={testData.pallet_num} onChange={v => updateField('pallet_num', v)} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <NumField label="КМ в коробке:" value={testData.km_in_box} onChange={v => updateField('km_in_box', v)} />
              <NumField label="КМ на паллете:" value={testData.km_on_pallet} onChange={v => updateField('km_on_pallet', v)} />
            </div>

            <Button variant="primary" className="w-full mt-3" onClick={renderPreview} isLoading={loading}>
              <RefreshCw className="h-4 w-4 mr-1" /> Обновить предпросмотр
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-0.5">{label}</label>
      <input
        type="text"
        className="w-full border rounded px-2 py-1 text-sm"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  )
}

function NumField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-0.5">{label}</label>
      <input
        type="number"
        className="w-full border rounded px-2 py-1 text-sm"
        value={value === 0 ? '' : String(value)}
        onChange={e => {
          const raw = e.target.value
          if (raw === '') {
            onChange(0)
          } else {
            const v = Number(raw)
            onChange(isNaN(v) ? 0 : v)
          }
        }}
      />
    </div>
  )
}
